import * as React from 'react'
import { cn } from '@dc-shared-web/lib'
import { Icon } from '@dc-shared-web/components/base/Icon'
import {
  IconDefinition,
  faCircleExclamation,
  faXmark,
} from '@document-crunch/pro-regular-svg-icons'
import { Label } from '@dc-shared-web/components/shadcn/label'
import { SizeProp } from '@fortawesome/fontawesome-svg-core'

type Size = 'lg' | 'md' | 'sm'

//added "size" to override the default size property on InputHTMLAttributes
type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  clearInputEnabled?: boolean
  leadingIcon?: IconDefinition
  icon?: IconDefinition
  iconAction?: () => void
  inputClassName?: string
  datatestid?: string
  size?: 'lg' | 'md' | 'sm'
  label?: string
  helpText?: string
  placeholder?: string
  error?: boolean
  disabled?: boolean
}

const sizeMap: Record<
  Size,
  {
    padding: string
    iconSize: SizeProp
    inputTextClassName: string
    iconSizeClassName: string
  }
> = {
  lg: {
    padding: 'tw-pl-8',
    iconSize: 'xs',
    iconSizeClassName: 'tw-w-4 tw-h-4',
    inputTextClassName: 'tw-h-10 tw-text-base',
  },
  md: {
    padding: 'tw-pl-7.5',
    iconSize: 'sm',
    iconSizeClassName: 'tw-w-3.5 tw-h-3.5',
    inputTextClassName: 'tw-h-9 tw-text-sm',
  },
  sm: {
    padding: 'tw-pl-7.5',
    iconSize: '1x',
    iconSizeClassName: 'tw-w-3 tw-h-3',
    inputTextClassName: 'tw-h-8 tw-text-2xs',
  },
}

const iconPlacementClassName =
  'tw-absolute tw-top-1/2 tw-transform -tw-translate-y-1/2'
const iconColorClassName =
  'tw-text-neutral-500 group-focus-within:tw-text-neutral-700 group-data-[disabled=true]:tw-text-neutral-400'

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size = 'md',
      clearInputEnabled,
      leadingIcon,
      icon,
      iconAction,
      inputClassName,
      datatestid,
      label,
      helpText,
      placeholder,
      error,
      disabled,
      ...props
    },
    ref
  ) => {
    const [inFocus, setInFocus] = React.useState(false)

    const handleClearClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()

      if (props.onChange) {
        const syntheticEvent = {
          target: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>
        props.onChange(syntheticEvent)
      }
    }

    return (
      <div
        data-disabled={disabled}
        className='tw-group tw-flex tw-flex-grow tw-flex-col tw-gap-1'
      >
        {label && (
          <Label className='group-data-[disabled=true]:tw-text-neutral-400 tw-text-neutral-700 tw-text-2xs'>
            {label}
          </Label>
        )}
        <div
          className={cn(
            'tw-relative tw-text-neutral-600',
            className,
            sizeMap[size as Size].inputTextClassName
          )}
        >
          {leadingIcon && (
            <Icon
              className={cn(
                iconPlacementClassName,
                iconColorClassName,
                sizeMap[size as Size].iconSizeClassName,
                'tw-left-2'
              )}
              icon={leadingIcon}
            />
          )}
          <input
            data-testid={datatestid}
            type={type}
            className={cn(
              'tw-flex tw-h-full tw-w-full tw-p-2 tw-rounded-md tw-border tw-border-neutral-100 hover:tw-border-neutral-200',
              'tw-flex tw-h-full tw-w-full tw-rounded-md tw-border tw-border-neutral-100 hover:tw-border-neutral-200 tw-bg-white hover:tw-bg-neutral-50 tw-px-3 tw-py-2 file:tw-border-0 file:tw-bg-transparent file:tw-text-sm file:tw-font-medium placeholder:tw-text-neutral-500 tw-text-neutral-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring disabled:tw-cursor-not-allowed disabled:tw-pointer-events-none disabled:tw-bg-neutral-100 disabled:placeholder:tw-text-neutral-400 disabled:tw-text-neutral-400 data-[error=true]:tw-border-destructive-600',
              (clearInputEnabled || icon) && 'tw-pr-12',
              inputClassName,
              leadingIcon && `${sizeMap[size as Size].padding}`
            )}
            onFocus={() => setInFocus(true)}
            onBlur={() => setInFocus(false)}
            ref={ref}
            placeholder={placeholder}
            data-error={error}
            disabled={disabled}
            {...props}
          />
          {!icon && clearInputEnabled && props.value && (
            <button
              type='button'
              className={cn(
                iconPlacementClassName,
                inFocus && 'tw-opacity-100',
                'tw-right-2'
              )}
              onMouseDown={handleClearClick}
              disabled={disabled}
            >
              <Icon
                className={cn(
                  iconColorClassName,
                  sizeMap[size].iconSizeClassName
                )}
                icon={faXmark}
              />
            </button>
          )}
          {icon && (
            <button
              type='button'
              className={cn(
                iconColorClassName,
                iconPlacementClassName,
                'tw-right-2'
              )}
              onClick={iconAction}
            >
              <Icon
                className={cn(
                  iconColorClassName,
                  sizeMap[size].iconSizeClassName
                )}
                icon={icon}
              />
            </button>
          )}
        </div>
        {(helpText || error) && (
          <Label
            className={cn(
              'tw-font-normal tw-text-2xs tw-inline-flex tw-items-center tw-gap-x-1 tw-h-4.5',
              helpText ? 'tw-visible' : 'tw-invisible',
              error
                ? 'tw-text-destructive-600'
                : 'tw-text-neutral-500 group-data-[disabled=true]:tw-text-neutral-400'
            )}
          >
            {error && helpText && (
              <Icon
                className='tw-text-destructive-600 tw-text-sm'
                icon={faCircleExclamation}
              />
            )}
            {helpText}
          </Label>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }
